import type * as React from "react";
import { cn } from "@/lib/utils";

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-surface text-card-foreground shadow-custom flex flex-col rounded-[12px] px-3 py-3 pl-3",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn("flex flex-col items-start gap-0", className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-lg leading-none font-extrabold text-neutral-800",
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-xs text-neutral-400", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

// Stepper-specific Card component
interface CardStepperProps extends React.ComponentProps<"div"> {
  collapsed?: boolean;
  width?: {
    expanded: string;
    collapsed: string;
  };
}

function CardStepper({
  className,
  collapsed = false,
  width = { expanded: "w-64", collapsed: "w-16" },
  ...props
}: CardStepperProps) {
  return (
    <div
      className={cn(
        "border-default bg-surface flex flex-col border-r transition-[width,border-color] duration-300 ease-in-out",
        collapsed ? width.collapsed : width.expanded,
        className,
      )}
      {...props}
    />
  );
}

// Attach Stepper as a subcomponent to Card
Card.Stepper = CardStepper;

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  type CardStepperProps,
};
